<!--src/views/PersonalCenter.vue-->
<template>
  <div class="personal-center-container">
    <!-- 顶部导航栏：新增返回首页按钮 -->
    <div class="page-header">
      <button
        class="back-btn"
        @click="$router.push('/')"
      >
        <i class="bi bi-arrow-left" />
      </button>
      <h2 class="page-title">
        个人中心
      </h2>
      <button
        class="setting-btn"
        @click="$router.push('/AccountSetting')"
      >
        <i class="bi bi-gear" />
      </button>
    </div>

    <!-- 用户信息卡片 -->
    <UserInfoCard />

    <!-- 功能菜单 -->
    <FunctionMenu
      :menu-list="menuList"
      :count-map="countMap"
      :is-login="hasLogin"
      @menu-click="handleMenuClick"
    />

    <!-- 未登录提示 -->
    <div
      v-if="!hasLogin"
      class="login-tip"
    >
      <p>登录后可查看更多个人数据~</p>
      <button
        class="btn-login"
        @click="handleLogin"
      >
        立即登录
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import UserInfoCard from "@/components/Common/UserInfoCard.vue"
import FunctionMenu from "@/components/Common/FunctionMenu.vue"
import { getUserInfo } from '@/api/user'
import { batchAddFavorite } from '@/api/favorite'
// 1. 导入 Pinia 全局用户仓库和 storeToRefs（用于响应式获取状态）
import { useUserStore } from '@/store/modules/userStore'
import { storeToRefs } from 'pinia'

const router = useRouter()
// 2. 获取 Pinia 全局用户仓库实例
const userStore = useUserStore()
// 3. 响应式获取仓库中的状态（必须用 storeToRefs 保证状态变化时组件自动更新）
const { hasLogin, userInfo } = storeToRefs(userStore)

// 保留原有业务数据
const menuList = ref([
  { icon: 'bi bi-list-check', label: '我的订单', path: '/MyOrder', key: 'order' },
  { icon: 'bi bi-wallet', label: '我的返现', path: '/MyBill', key: 'bill' },
  { icon: 'bi bi-star', label: '我的收藏', path: '/favorite', key: 'favorite' },
  { icon: 'bi bi-ticket-perforated', label: '领券通道', path: '/GetCoupons', key: 'coupon' },
  { svgIcon: '/images/icon-strategy.svg', label: '攻略中心', path: '/Strategy', key: 'strategy' },
  { icon: 'bi bi-gear-wide-connected', label: '账号设置', path: '/AccountSetting', key: 'setting' }
])
const countMap = ref({ order: 0, favorite: 0 })

// 3. 同步游客收藏到登录账号（保留原有业务逻辑，适配 Pinia 状态）
const syncLocalFavorites = async () => {
  try {
    // 仅当已登录时执行同步
    if (!hasLogin.value) return

    // 获取本地收藏的商品信息
    let localFavs = JSON.parse(localStorage.getItem('favoriteProducts') || '{}')
    // 兼容 favoriteManager 的对象格式，转成数组
    if (!Array.isArray(localFavs)) {
      localFavs = Object.values(localFavs).filter(item => item?.isFavorite).map(item => ({
        skuId: item.skuId || item.productId,
        platform: item.platform,
        goods_name: item.goods_name || '',
        goods_img: item.goods_img || '',
        price: item.price || '0.00'
      }));
    }
    if (!Array.isArray(localFavs) || localFavs.length === 0) return

    // 批量同步到服务器
    const res = await batchAddFavorite({ syncData: localFavs })
    if (res.code === 200) {
      // 同步成功后清空本地收藏
      localStorage.removeItem('favoriteProducts')
      ElMessage.success(`已同步${localFavs.length}个本地收藏到账号`)
    }
  } catch (err) {
    console.warn('同步本地收藏失败:', err)
  }
}

// 4. 适配 Pinia 的初始化方法（复用全局状态初始化，保留原有业务）
const initData = async () => {
  // 调用 Pinia 全局统一的状态初始化方法（确保状态最新）
  await userStore.initUserStatus()

  // 打印调试信息（适配 Pinia 状态，方便排查）

  // 已登录时，执行本地收藏同步（保留原有业务）
  if (hasLogin.value) {
    await syncLocalFavorites()

    // 可选：重新获取用户最新信息（兜底，确保状态一致）
    try {
      const res = await getUserInfo();
      if (res.code === 0) {
        // 直接更新 Pinia 全局状态（所有组件会实时同步）
        userStore.userInfo = {
          ...userInfo.value,
          avatar: res.data.avatar || userInfo.value.avatar,
          nickname: res.data.username || userInfo.value.nickname,
          phone: res.data.phone || userInfo.value.phone
        }

        // 同步更新本地存储（确保刷新后状态不丢失）
        const userInfoLocal = JSON.parse(localStorage.getItem('userInfo') || '{}')
        localStorage.setItem('userInfo', JSON.stringify({
          ...userInfoLocal,
          ...userStore.userInfo
        }))
      }
    } catch (err) {
      console.warn('【调试】获取用户信息失败:', err);
    }
  }
}

// 5. 登录/退出/菜单逻辑（适配 Pinia 全局状态）
const handleLogin = () => {
  // 跳转登录页，记录返回地址（方便登录后返回个人中心）
  router.push({ path: '/login', query: { redirect: '/personalcenter' } })
}

// 保留原有菜单点击逻辑，适配 Pinia 的 hasLogin 状态
const handleMenuClick = (menu) => {
  if (!hasLogin.value) {
    if (['order', 'bill'].includes(menu.key)) {
      // 订单/账单：提示并跳转登录
      ElMessage.warning('请先登录');
      handleLogin();
    } else if (menu.key === 'favorite') {
      // 收藏：允许访问（未登录也能收藏）
      router.push(menu.path);
    } else {
      // 其他菜单：正常跳转
      router.push(menu.path);
    }
    return;
  }
  router.push(menu.path);
};

// 6. 生命周期与监听（适配 Pinia 状态，保留原有监听逻辑）
onMounted(() => {
  // 初始化数据（复用 Pinia 全局逻辑）
  initData();

  // 监听 localStorage 变化（同步其他页面修改的状态）
  window.addEventListener('storage', initData);
});

// 组件卸载时移除监听，防止内存泄漏
onUnmounted(() => {
  window.removeEventListener('storage', initData);
});

// 监听路由变化 + Pinia 状态变化（确保状态实时同步）
watch([() => router.currentRoute.path, () => hasLogin.value], () => {
  initData();
}, { deep: true });
</script>

<style scoped lang="scss">
// 样式保持不变，无需修改
@import "@/assets/css/mixin.scss";

.personal-center-container {
  padding: 0;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #faf3fc;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #faf3fc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 99;

  .back-btn {
    padding: 8px;
    border: none;
    border-radius: 8px;
    background-color: #fff;
    color: #666;
    cursor: pointer;
    margin-right: 12px;
    transition: all 0.2s;

    &:hover {
      color: var(--el-color-primary);
      background-color: rgba(var(--el-color-primary-rgb), 0.05);
    }

    .bi {
      font-size: 18px;
    }
  }

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    flex: 1;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  .setting-btn {
    padding: 8px;
    border: none;
    border-radius: 8px;
    background-color: #fff;
    color: #666;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      color: var(--el-color-primary);
      background-color: rgba(var(--el-color-primary-rgb), 0.05);
    }

    .bi {
      font-size: 18px;
    }
  }
}

.login-tip {
  text-align: center;
  padding: 40px 20px;
  margin: 20px 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);

  p {
    color: #9ca3af;
    font-size: 15px;
    margin: 20px 0;
  }

  .btn-login {
    padding: 10px 24px;
    @include bg_color;
    @include font_color;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  }
}
</style>
